import { useContext, useEffect, useRef } from "react";
import queryString from "query-string";
import { useConfirm } from "material-ui-confirm";
import history from "../../helpers/history";
import { AuthContext } from "../../contexts/Auth";
import { hasPermission } from "../../helpers/authorization";
import {
  RESUME_PARAM,
  fetchActivePause,
  pausedUntilText,
  spanText,
  unpauseDevice,
} from "../../helpers/notificationPause";

const dialog = {
  title: null,
  dialogProps: { fullScreen: false },
  cancellationButtonProps: { color: "error", disableRipple: true },
  confirmationButtonProps: { color: "primary", disableRipple: true },
};

/*
 * The "Resume notifications" button in the pause email opens the asset with
 * ?resume=1. On a phone the small Resume link is easy to miss, so this asks
 * straight away, Yes or No, the same way the Alerts toggle asks. The parameter
 * is dropped first, so a refresh or Back doesn't ask again. Renders nothing.
 */
export default function UnpausePrompt({ deviceId, assetName }) {
  const confirm = useConfirm();
  const { state: currentUser } = useContext(AuthContext);
  const asked = useRef(false);

  useEffect(() => {
    const params = queryString.parse(history.location.search);
    if (!(RESUME_PARAM in params) || !deviceId || asked.current) return;
    asked.current = true;

    delete params[RESUME_PARAM];
    const search = queryString.stringify(params);
    history.replace({
      pathname: history.location.pathname,
      search: search ? `?${search}` : "",
    });

    if (!hasPermission(currentUser?.user?.perms || [], "DEVICE_R")) return;

    const name = assetName || "this device";
    const tell = (description) =>
      confirm({
        ...dialog,
        description,
        confirmationText: "OK",
        hideCancelButton: true,
      }).catch(() => {});

    (async () => {
      const pause = await fetchActivePause(deviceId);
      if (!pause) {
        await tell(`Impact notifications for ${name} aren't paused anymore.`);
        return;
      }
      try {
        await confirm({
          ...dialog,
          description: `Are you sure you want to resume impact notifications for ${name}?`,
          confirmationText: "Yes",
          cancellationText: "No",
          // Only Yes or No answers it: a stray tap beside the dialog or Escape mustn't count as No.
          allowClose: false,
        });
      } catch (e) {
        return; // No
      }
      if (!(await unpauseDevice(deviceId))) {
        await tell(
          `Couldn't resume impact notifications for ${name}. Try again with Resume next to Alerts.`,
        );
      }
    })();
    // Asks once, when the device has loaded; later changes to the rest don't ask again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  return null;
}
